import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';
import {
	isIssueTypeNameFilter,
	type ConnectionFieldIssueTypeFilter,
	type ConnectionFieldIssueViewLayoutSort,
} from '@jira/polaris__domain-field/src/ConnectionFieldIssueTypeFilter.types.tsx';
import type {
	ConnectionFieldConfiguration,
	FieldKey,
} from '@jira/polaris__domain-field/src/FieldKey.types.tsx';

import { SUMMARY_FIELDKEY } from '@jira/polaris__domain-field/src/issuetypeFieldkey.constants.tsx';
import { createJsonPatch } from '@jira/polaris__lib-json-patch/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { createGetFieldByKey } from './createGetFieldByKey.tsx';
import type { Props, State } from './FieldsUpdate.types.tsx';

export const updateConnectionFieldFilter =
	(fieldKey: FieldKey, filter: ConnectionFieldIssueTypeFilter | null) =>
	async (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, fieldRemote }: Props,
	): Promise<void> => {
		const state = getState();
		const currentField = createGetFieldByKey(fieldKey)(state);

		if (!currentField) {
			return;
		}

		const newConfiguration: ConnectionFieldConfiguration = {
			...currentField.configuration,
			issueTypeFilters: filter && isIssueTypeNameFilter(filter) ? [filter] : [],
		};

		try {
			setState({
				fields: {
					...state.fields,
					[fieldKey]: {
						...currentField,
						configuration: newConfiguration,
					},
				},
			});

			await fieldRemote.updateFieldConfiguration({
				fieldKey,
				oldConfiguration: currentField.configuration || {},
				configuration: newConfiguration,
				type: 'updateConnectionField',
			});
		} catch (error) {
			setState({
				fields: {
					...state.fields,
					[fieldKey]: currentField,
				},
			});

			if (error instanceof Error) {
				logSafeErrorWithoutCustomerDataWrapper(
					'polaris.error.controllers.field.actions.update-field-configuration.connection.issue-filters',
					'Failed to update connection field issue filters',
					error,
				);

				onFieldUpdateFailed(error);
			}
		}
	};

export type ConnectionFieldIssueViewLayout = {
	selectedFieldKey?: FieldKey | null;
	sort?: ConnectionFieldIssueViewLayoutSort | null;
};

export const updateConnectionFieldIssueViewLayout =
	(fieldKey: FieldKey, config: ConnectionFieldIssueViewLayout) =>
	async (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, fieldRemote, globalFieldRemote, isSharedView }: Props,
	): Promise<void> => {
		const state = getState();
		const currentField = createGetFieldByKey(fieldKey)(state);

		if (!currentField) {
			return;
		}

		const currentConfiguration = currentField.configuration || {};

		let issueViewLayoutFields: FieldKey[];
		if (config.selectedFieldKey === undefined) {
			issueViewLayoutFields = currentConfiguration.issueViewLayout?.fields || [];
		} else if (config.selectedFieldKey === null) {
			issueViewLayoutFields = [];
		} else {
			issueViewLayoutFields = [config.selectedFieldKey];
		}

		let issueViewLayoutSort: ConnectionFieldIssueViewLayoutSort[];
		if (
			config.selectedFieldKey === null &&
			currentConfiguration.issueViewLayout?.sort?.[0]?.fieldKey !== SUMMARY_FIELDKEY
		) {
			issueViewLayoutSort = [];
		} else if (config.sort === undefined) {
			issueViewLayoutSort = currentConfiguration.issueViewLayout?.sort || [];
		} else if (config.sort === null) {
			issueViewLayoutSort = [];
		} else {
			issueViewLayoutSort = [config.sort];
		}

		const newConfiguration: ConnectionFieldConfiguration = {
			...currentConfiguration,
			issueViewLayout: {
				fields: issueViewLayoutFields,
				sort: issueViewLayoutSort,
			},
		};

		try {
			setState({
				fields: {
					...state.fields,
					[fieldKey]: {
						...currentField,
						configuration: newConfiguration,
					},
				},
			});

			if (isSharedView) {
				return;
			}

			if (currentField.global) {
				await globalFieldRemote.updateGlobalFieldConfiguration({
					fieldKey,
					patch: createJsonPatch(currentConfiguration, newConfiguration),
				});
			} else {
				await fieldRemote.updateFieldConfiguration({
					fieldKey,
					oldConfiguration: currentConfiguration,
					configuration: newConfiguration,
					type: 'updateConnectionField',
				});
			}
		} catch (error) {
			setState({
				fields: {
					...state.fields,
					[fieldKey]: currentField,
				},
			});

			if (error instanceof Error) {
				logSafeErrorWithoutCustomerDataWrapper(
					'polaris.error.controllers.field.actions.update-field-configuration.connection.issue-view-layout',
					'Failed to update connection field issue view layout',
					error,
				);

				onFieldUpdateFailed(error);
			}
		}
	};
